import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type { PageQuery, PageResult } from '../types/result';
import type { ConfirmResult } from '../types/inventoryDoc';

export interface TransferRow {
  id: string;
  bizNo: string;
  orgId: string;
  outWarehouseId: string;
  inWarehouseId: string;
  transferType: string | null;
  transferStatus: string;
  sourceSupplyOrderId: string;
  totalQty: number;
  totalAmount: number;
  bizDate: string;
  remark: string | null;
}

export interface TransferItemRow {
  id: string;
  transferId: string;
  lineNo: number;
  materialId: string;
  materialCode: string | null;
  materialName: string | null;
  unitId: string;
  unitName: string | null;
  qty: number;
  outQty: number;
  inQty: number;
  batchNo: string | null;
  price: number;
  amount: number;
}

export interface TransferDetail {
  transfer: TransferRow;
  items: TransferItemRow[];
}

export interface TransferListQuery extends PageQuery {
  transferStatus?: string;
  outWarehouseId?: string;
}

export interface StocktakeRow {
  id: string;
  bizNo: string;
  orgId: string;
  warehouseId: string;
  /** 仓库名称（后端二次查询补全；旧数据可能为空） */
  warehouseName?: string;
  takeType: string | null;
  takeMode: string | null;
  takeStatus: string | null;
  planBeginAt: string | null;
  planEndAt: string | null;
  totalDiffAmount: number;
  bizDate: string;
  remark: string | null;
}

export interface StocktakeItemRow {
  id: string;
  stocktakeId: string;
  materialId: string;
  materialCode: string | null;
  materialName: string | null;
  unitId: string;
  unitName: string | null;
  batchNo: string | null;
  bookQty: number;
  countQty: number;
  diffQty: number;
  diffAmount: number;
  diffReason: string | null;
  handleType: string | null;
}

export interface StocktakeDetail {
  stocktake: StocktakeRow;
  items: StocktakeItemRow[];
}

export interface StocktakeListQuery extends PageQuery {
  takeStatus?: string;
  warehouseId?: string;
}

/** 建盘点单：按仓库账面库存生成明细（OPEN 明盘预填 / BLIND 盲盘留空）。 */
export interface StocktakeCreateRequest {
  warehouseId: string;
  takeType?: 'FULL' | 'CYCLE' | 'SPOT' | 'RFID';
  takeMode?: 'OPEN' | 'BLIND';
  categoryId?: string;
  planBeginAt?: string;
  planEndAt?: string;
  remark?: string;
}

export interface StocktakeCreateResult {
  id: string;
  bizNo: string;
  orgId: string;
  warehouseId: string;
  takeType: string;
  takeMode: string;
  status: string;
  /** 生成的明细行数（账面库存条数）。 */
  itemCount: number;
  planBeginAt: string | null;
  planEndAt: string | null;
}

/** 实盘录入：只改实盘数，差异在确认时统一核算。 */
export interface StocktakeCountRequest {
  countQty: number;
  diffReason?: string;
  handleType?: 'ADJUST' | 'RECOUNT' | 'KEEP';
}

export interface StocktakeCountResult {
  itemId: string;
  stocktakeId: string;
  bookQty: number;
  countQty: number;
  diffQty: number;
  handleType: string;
}

/** 仓间调拨与库存盘点。 */
export function createInventoryOpsApi(http: HttpClient) {
  return {
    transfer: {
      list(query: TransferListQuery = {}): Promise<PageResult<TransferRow>> {
        return http.request<PageResult<TransferRow>>({ path: '/wl/transfer', query: toQuery(query) });
      },
      detail(id: Id): Promise<TransferDetail> {
        return http.request<TransferDetail>({ path: `/wl/transfer/${id}` });
      },
      /** 确认调拨：出仓扣减 + 入仓增加（同一事务）。 */
      confirm(id: Id): Promise<ConfirmResult> {
        return http.request<ConfirmResult>({ path: `/wl/transfer/${id}/confirm`, method: 'POST' });
      }
    },

    stocktake: {
      list(query: StocktakeListQuery = {}): Promise<PageResult<StocktakeRow>> {
        return http.request<PageResult<StocktakeRow>>({ path: '/wl/stocktake', query: toQuery(query) });
      },
      detail(id: Id): Promise<StocktakeDetail> {
        return http.request<StocktakeDetail>({ path: `/wl/stocktake/${id}` });
      },
      /** 建盘点单并自动按账面库存生成明细（移动端扫码盘点入口）。 */
      create(body: StocktakeCreateRequest): Promise<StocktakeCreateResult> {
        return http.request<StocktakeCreateResult>({ path: '/wl/stocktake', method: 'POST', body });
      },
      /** 录入单行实盘数（可反复覆盖，确认时统一算差异）。 */
      count(id: Id, itemId: Id, body: StocktakeCountRequest): Promise<StocktakeCountResult> {
        return http.request<StocktakeCountResult>({
          path: `/wl/stocktake/${id}/items/${itemId}`,
          method: 'PUT',
          body
        });
      },
      /** 确认盘点：按实盘数调账并写流水。 */
      confirm(id: Id): Promise<{ id: string; bizNo: string; status: string; diffAmount: number }> {
        return http.request({ path: `/wl/stocktake/${id}/confirm`, method: 'POST' });
      }
    }
  };
}

export type InventoryOpsApi = ReturnType<typeof createInventoryOpsApi>;
